// ── src/billing.js ───────────────────────────────────────────────────────────
// A BILL IS NOT A PREMIUM, AND IT IS SETTLED PER POLICY.
//
// `crop_coverage.net_premium` (gross − subsidy) ties to the AIP's statement
// exactly — verified 2026-08-26 on Anchor Farms NY-944-6101410-26, every line
// to the dollar. What the grower is actually BILLED is a third number:
//
//     balance due = farmer premium + administrative fees − claim credits
//
// Neither of the last two was anywhere on screen, so a farm that took a claim
// read HIGH against its own bill by exactly the credit. Anchor read $15,439
// against a bill of $8,178, and the $7,381 gap was a prevent-plant indemnity
// NAU had already applied against his premium.
//
// ⚠ THE GRAIN IS THE POLICY, NOT THE COUNTY, AND THAT WAS MEASURED THE HARD WAY.
// The first cut of this module reconciled per COUNTY — Anchor is single-county,
// so it tied to his statement perfectly and looked right. Run over the whole
// CY2026 book it put FIVE cards into negative balance. The cause: a credit pays
// down the POLICY, while `indemnities.county` names where the LOSS happened.
// Fire Creek Farms MP-0935914 spans LIVINGSTON and ONTARIO; its $6,167 credit is
// exactly the whole policy's producer premium, and county-scoping shoved all of
// it against Ontario's $2,625. Grouped by policy the identity is exact and
// book-wide: `credit − policy net premium` is $0, $30, $60 or $90 — the premium
// plus N administrative fees — on every fully-credited policy, multi-county ones
// included. That is also the shape the statement itself prints: "Policy Totals".
//
// Scoping to the policy also DELETES a whole class of guesswork. A credit joins
// by `policy_id`, which the AIP stamped; nothing has to be inferred from a crop
// or county string, and the "CORN, SOYBEANS" / "LIVINGSTON, MONROE" joined
// fields become labels rather than keys.
//
// THE CREDITS ARE SOURCED, THE FEES ARE MODELLED, AND THE SCREEN SAYS WHICH.
//
//   * CREDITS come from M13 P20A `loss_code` — real disbursement rows we
//     already store. M is the PRODUCER PREMIUM credit and F is the
//     ADMINISTRATIVE FEE credit; they are NOT the same thing, measured
//     book-wide 2026-08-26: 151 of 162 F rows are exact multiples of $30
//     while only 4 of 199 M rows are, and M values look like premiums
//     (43.94, 287.34, up to 119,498). Anchor decomposes to the dollar —
//     corn M $7,351 (= GSG 6,710 + GSS 641) + F $30 = the bill's $7,381
//     loss credit, and wheat M $416 + F $30 = its $446.
//
//   * FEES ARE NOT IN THE FILE. Scanned every field position of NAU CY2026
//     P11 (8,580 rows) and P14 (1,586 rows) in `m13_*`: no position holds 30
//     on more than 42 rows, and `crop_coverage` has no fee column. So the fee
//     is the ONE modelled input here, and it is rendered as its own line with
//     its own arithmetic showing ("4 × $30") rather than folded into a total —
//     a wrong fee count must be visible as a wrong fee count, not as a wrong
//     balance.
//
// ⚠ THE FEE RULE IS VALIDATED AGAINST THE AIP'S OWN NUMBER, not merely reasoned
// about. Where a claim credited a policy IN FULL, the AIP's F disbursement is
// its own statement of how many fees that policy owed. Measured on the live
// CY2026 book 2026-08-26: of the 13 fully-credited policies, **12 have F ÷ 30
// exactly equal to the count of (county × crop × plan) lines this module bills**
// — including a 19-line policy and a 15-line one. That is the rule reproducing
// the carrier's arithmetic, not being fitted to it.
//
// The 13th (Meisenzahl NY-944-1021879-26) credits 10 fees against 6 modelled
// lines and lands at −$120. It is NOT silently absorbed: `overCredited` fires
// and the screen prints "credit exceeds the bill". One unexplained policy that
// says so is worth more than 342 that quietly look tidy.
//
// THE TWO CARRIERS DIFFER AND BOTH ARE HANDLED: NAU credited Anchor's
// administrative fee back (an F row beside the M); R&H credited none of Fire
// Creek's. So the fee really is still owed on some settled policies, and
// modelling it is what stands between Fire Creek's balance and a $360 error.
//
// The fee AMOUNT is not invented either: it is the same rule the MPCI quoter
// already ships in src/mpciEngine.js ($655 CAT / $30 buy-up, per crop per
// county), sourced there to RMA MGR-19-006 and to the FEES column of the R&H
// Schedules of Insurance in this book. ONE rule, two callers — this repo has
// been bitten repeatedly by a rule defined twice (three news handlers, two
// guarantee engines, three normFarmerName copies), so do not re-declare it.

export const ADMIN_FEE_BUYUP = 30;
export const ADMIN_FEE_CAT = 655;

// Credit-bearing P20A loss codes. M = producer premium, F = administrative fee.
// Both settle WITH the claim, so both are money the grower never had to pay.
export const CREDIT_LOSS_CODES = { M: "premium credit", F: "fee credit" };

const up = (v) => String(v == null ? "" : v).trim().toUpperCase();
// Money in integer cents. This number is a bill, and float drift in a
// subtraction chain is what the cash-target engine already measured
// (0.28000000000000025); the fix there is the fix here.
const cents = (v) => Math.round((Number(v) || 0) * 100);
const dollars = (c) => c / 100;

// CAT is decided by the AIP's own coverage type where it states one. The
// 50%/55% shape is the fallback and agrees with it on every row in the book
// (36 of 36 CY2026 `coverage_type='C'` rows carry exactly 0.50 / 0.55), so a
// carrier that stops stamping the column cannot silently make CAT look like
// buy-up and undercharge the fee by $625.
export function isCatCoverage(row) {
  if (!row) return false;
  if (up(row.coverageType ?? row.coverage_type) === "C") return true;
  const cl = Number(row.coverageLevel ?? row.coverage_level);
  const pe = Number(row.priceElectionPct ?? row.price_election_pct);
  return Math.abs(cl - 0.5) < 1e-6 && Math.abs(pe - 0.55) < 1e-6;
}

// ONE FEE PER (COUNTY, CROP, PLAN), NOT PER ROW.
//
// RMA charges the administrative fee per crop PER COUNTY, so a policy spanning
// two counties owes two fees on the same crop — dropping the county from the
// key under-bills every multi-county policy in the book, and there are several
// (Fire Creek MP-0935914 spans two, MP-0876033 spans three).
//
// Within one county, corn GSG and corn GSS are two rows of ONE billed coverage
// — the AIP prints them as a single "CORN RP 75/100" line carrying a single $30
// — so keying the fee on the TYPE would charge Anchor twice for one corn line.
// An area-plan overlay DOES carry its own fee: his statement charges $30 on
// CORN ECO-RP and $30 on SOYBEANS ECO-RP beside the two base lines, 4 × $30 =
// the $120 the policy totals show. That is the AIP stating it, not us inferring.
//
// A group with NO GROSS PREMIUM is not billed and takes no fee. That is what
// keeps a coverage whose acreage is reported but not yet keyed — $0 on every
// line — from inventing a $30 charge. CAT survives that test: its premium is
// 100% subsidised so its NET is $0, but its GROSS is not.
export function adminFeeLines(rows) {
  const seen = new Map();
  (rows || []).forEach((r) => {
    if (!r) return;
    if (!(Number(r.totalPremium ?? r.total_premium) > 0)) return;
    const county = up(r.county);
    const crop = up(r.crop);
    const plan = up(r.plan) || "?";
    const key = county + "|" + crop + "|" + plan;
    const cat = isCatCoverage(r);
    const prev = seen.get(key);
    // Within one billed line, CAT wins — a $655 line must never be reported at
    // $30 because a sibling row was stamped differently.
    if (!prev) seen.set(key, { county, crop, plan, cat });
    else if (cat && !prev.cat) prev.cat = true;
  });
  return [...seen.values()]
    .map((l) => ({ ...l, fee: l.cat ? ADMIN_FEE_CAT : ADMIN_FEE_BUYUP }))
    .sort((a, b) => a.county.localeCompare(b.county) || a.crop.localeCompare(b.crop) || a.plan.localeCompare(b.plan));
}

// A claim names its crops and counties as the AIP joined them — "CORN,
// SOYBEANS", "LIVINGSTON, MONROE". Those are LABELS here, never keys: a
// disbursement carries no per-cell breakdown in M13, so splitting one across
// the cells it names would be inventing a number. The policy is the key.
export function splitNames(value) {
  return up(value).split(",").map((s) => s.trim()).filter(Boolean);
}
export function claimCrops(claim) { return splitNames(claim && claim.crop); }
export function claimCounties(claim) { return splitNames(claim && claim.county); }

// CREDITS JOIN BY POLICY — the id the AIP itself stamped on the disbursement.
// Nothing is inferred, so nothing can be mis-attributed, double-counted across
// cards, or silently dropped. A claim whose crop year differs from the bill's is
// excluded: the sync's documented -26 → -25 fallback files a prior-year claim
// against a current-year policy row, and last year's credit paid last year's
// bill.
export function creditsForPolicy(claims, { policyId, policyNumber, cropYear }) {
  const pid = policyId == null ? null : String(policyId);
  const pnum = policyNumber ? up(policyNumber) : null;
  return (claims || []).filter((c) => {
    if (!c) return false;
    if (!CREDIT_LOSS_CODES[up(c.lossCode)]) return false;
    if (up(c.policyType || "crop") !== "CROP") return false;
    if (cropYear != null && Number(c.cropYear) !== Number(cropYear)) return false;
    if (!(Number(c.indemnityAmount) > 0)) return false;
    if (pid != null) return String(c.policyId) === pid;
    if (pnum) return up(c.policyNumber) === pnum;
    return false;   // No policy to join on is not a licence to credit everything.
  }).map((c) => ({
    claimNumber: c.claimNumber || null,
    lossCode: up(c.lossCode),
    kind: CREDIT_LOSS_CODES[up(c.lossCode)],
    crops: claimCrops(c),
    counties: claimCounties(c),
    crop: c.crop || null,
    county: c.county || null,
    stages: c.claimStages || null,
    amount: Number(c.indemnityAmount) || 0,
  }));
}

// ⚠ THE OVERLAYS DO NOT LIVE WITH THE BASE COVERAGES, AND THE FIRST CUT BILLED
// WITHOUT THEM. `farmer.approvedYields` holds base MPCI only; SCO/ECO land in
// `farmer.scoEcoEndorsements` (the data layer splits them — see the comment at
// `scoEcoSpring` in App.jsx). Assembling a policy's rows from approvedYields
// alone billed Anchor Farms at **$11,967 over 2 fee lines** where his statement
// says **$15,439 over 4** — his two ECO-RP lines ($2,427 + $1,045 of producer
// premium, and $60 of fee) were simply absent. The county table beside it was
// right the whole time, because it unions `[...rpRows, ...scoRows, ...ecoRows]`.
//
// So the assembly is a RULE, not something each caller does for itself: the
// panel and the farmer PDF both call this. Two copies of "which rows are this
// policy's rows" is exactly how one surface comes to bill a farm differently
// from the surface above it.
export function billingRowsForTab({ approvedYields, scoEcoEndorsements, crops, cropYear }) {
  const cropSet = new Set((crops || []).map(up));
  // A row states its own crop year; keep it only when it agrees with the bill's.
  // Written as "differs" rather than "equals" so a row with no stated year — or
  // a card with no stated year — is kept, never silently dropped.
  const wrongYear = (r) => cropYear != null && r && r.cropYear != null && Number(r.cropYear) !== Number(cropYear);
  const all = [...(approvedYields || []), ...(scoEcoEndorsements || [])]
    .filter((r) => r && !wrongYear(r));
  const onTab = all.filter((r) => cropSet.has(up(r.crop)));
  // The TAB decides which policies are spoken for; the POLICY decides which rows
  // are billed. A policy whose only coverage on this tab is an overlay still has
  // a bill, so the ids come from the combined set.
  const ids = [...new Set(onTab.map((r) => r.policyId).filter(Boolean))];
  return ids.map((policyId) => {
    const rows = all.filter((r) => r.policyId === policyId);
    return {
      policyId,
      policyNumber: (rows.find((r) => r.policyNumber) || {}).policyNumber || null,
      rows,
      // TRUE when this policy also wrote coverage the current tab does not show.
      // The bill is the whole policy, so the number is right either way — but the
      // reader has to be told, or a spring card silently prints a total that
      // includes wheat.
      spansOtherTabs: rows.some((r) => !cropSet.has(up(r.crop))),
      farmerPremium: rows.reduce((s, r) => {
        if (r.netPremium != null) return s + Number(r.netPremium);
        if (r.totalPremium != null && r.subsidy != null) return s + Number(r.totalPremium) - Number(r.subsidy);
        return s + (r.totalPremium ? Number(r.totalPremium) : 0);
      }, 0),
    };
  });
}

// The reconciliation the AIP statement performs, in the AIP's own order, at the
// AIP's own grain. `farmerPremium` is passed in rather than re-derived: the
// card already computes it (net_premium, falling back to gross − subsidy) and a
// second copy of that fallback is how a footer comes to disagree with its rows.
export function buildBilling({ rows, claims, policyId, policyNumber, cropYear, farmerPremium }) {
  const feeLines = adminFeeLines(rows);
  const credits = creditsForPolicy(claims, { policyId, policyNumber, cropYear });

  const premC = cents(farmerPremium);
  const feesC = feeLines.reduce((s, l) => s + cents(l.fee), 0);
  const creditC = credits.reduce((s, c) => s + cents(c.amount), 0);
  const balanceC = premC + feesC - creditC;

  return {
    farmerPremium: dollars(premC),
    fees: dollars(feesC),
    feeLines,
    credits,
    creditTotal: dollars(creditC),
    balanceDue: dollars(balanceC),
    // NOT clamped at zero. A credit larger than what is owed means either the
    // AIP cut a check for the excess (it did — that is the E disbursement) or
    // our fee count is short, and both deserve to be looked at rather than
    // rounded away into a tidy $0.
    overCredited: balanceC < 0,
    // Nothing to reconcile is not the same as a $0 bill.
    hasAnything: premC > 0 || feesC > 0 || creditC > 0,
  };
}
